import { __decorate } from "tslib";
import { Model, modelRegistry } from './Model';
import { CashRegisterTransaction, Shift } from '../models';
import { PaymentTypeLabel } from '../types';
import { I18n, LocaleOverride } from '../wrappers/I18n';
import FiscalServiceLegacy from '../fiscal/FiscalServiceLegacy';
import Money from '../utilities/Money';
import Store from '../wrappers/Store';
import moment from 'moment-timezone';
export class CashRegister extends Model {
    static entity = 'cash_registers';
    id = null;
    hash = '';
    activation_code = null;
    name = null;
    uuid = null;
    platform = null;
    manufacturer = null;
    model = null;
    status = null;
    settings = { payments: {} };
    shared = false;
    warehouse_id = null;
    warehouse = null;
    created_at = null;
    updated_at = null;
    transactions = [];
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(''),
            activation_code: this.attr(null),
            name: this.attr(null),
            uuid: this.attr(null),
            platform: this.attr(null),
            manufacturer: this.attr(null),
            model: this.attr(null),
            status: this.attr(null),
            settings: this.attr({ payments: {} }),
            shared: this.attr(false, value => !!value),
            warehouse_id: this.attr(null),
            warehouse: this.attr(null, (value) => value ? { id: Number(value.id), name: String(value.name) } : null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            transactions: this.attr([], (value) => (value || []).map(item => new CashRegisterTransaction(item))),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    static active() {
        return CashRegister.all().filter(cashRegister => cashRegister.isActive());
    }
    isActive() {
        return this.status === 'active';
    }
    getFullName() {
        if (I18n.c('order.registerMethod') === 'legacy') {
            return `${this.name} / ${this.activation_code}`;
        }
        return this.name || `${I18n.t('general.cash_register')} / ${this.hash}`;
    }
    getWarehouseName() {
        return this.warehouse?.name || null;
    }
    getCreatedAt() {
        return moment.utc(this.created_at).local();
    }
    getTransactions() {
        return CashRegisterTransaction.all().filter(t => t.cash_register_id === this.id);
    }
    relevantTransactions() {
        return this.getTransactions();
    }
    relevantOrders() {
        const curShift = Shift.current();
        return curShift ? curShift.getOrders() : [];
    }
    getCash() {
        return this.relevantTransactions().reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0))
            .add(this.getPaidCash());
    }
    getPaidCash() {
        return this.relevantOrders()
            .filter(order => order.isCashExchanged())
            .reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0));
    }
    depositRequired() {
        if (!I18n.c('cashRegister.depositRequired')) {
            return false;
        }
        const tenant = Store.get('tenant');
        const noncashOnly = tenant && tenant.venue && !!tenant.venue.settings.noncash_only;
        if (noncashOnly) {
            return false;
        }
        const curDay = moment().tz('Europe/Podgorica').format('YYYY-MM-DD');
        const depositAdded = this.relevantTransactions().some(transaction => {
            if (transaction.getAmount().toUnit() < 0) {
                return false;
            }
            const issuedAt = moment.utc(transaction.issued_at).tz('Europe/Podgorica');
            return issuedAt.format('YYYY-MM-DD') === curDay;
        });
        return !depositAdded;
    }
    async addTransaction(value) {
        if (!Store.get('tenant')) {
            return { success: false };
        }
        let curShift = Shift.current();
        if (!curShift)
            return { success: false };
        const limit = I18n.c('cashRegister.transactionLimit');
        if (value > limit)
            return { success: false };
        if (I18n.c('cashRegister.depositRequired') && value > 0 && !this.depositRequired())
            return { success: false };
        if (!I18n.c('cashRegister.depositRequired') && value == 0)
            return { success: false };
        if (I18n.c('order.registerMethod') === 'legacy') {
            const result = value > 0
                ? await FiscalServiceLegacy.depositMoney(value)
                : await FiscalServiceLegacy.withdrawMoney(value);
            if (!result.success)
                return { success: false };
        }
        let transaction = new CashRegisterTransaction({
            cash_register_id: this.id,
            shift_id: curShift.id,
            shift_hash: curShift.hash,
            amount: Money.parse(value).toFormat('0.0000'),
            issued_at: moment().utc().format(CashRegisterTransaction.DATE_FORMAT),
            fiscal_code: null,
            register_status: null,
            registered_at: null
        });
        return transaction.sync();
    }
    static availablePaymentTypes() {
        const noncashOnly = !!Store.get('tenant')?.venue?.settings.noncash_only;
        const cashPaymentTypes = [PaymentTypeLabel.cash, PaymentTypeLabel.credit_card];
        return [
            ...(!noncashOnly ? cashPaymentTypes : []),
            PaymentTypeLabel.invoice,
            PaymentTypeLabel.advance,
            PaymentTypeLabel.check,
            PaymentTypeLabel.voucher,
            PaymentTypeLabel.mobile_money,
            PaymentTypeLabel.other_noncash,
        ].map(val => {
            return {
                key: val,
                display: I18n.t(`orders.payment_types.${val}`),
            };
        });
    }
    allowedPaymentTypes() {
        const availablePaymentTypes = CashRegister.availablePaymentTypes();
        return availablePaymentTypes.filter(type => !!this.settings.payments[type.key]);
    }
    allowsPayment(paymentType) {
        if (!Array.isArray(paymentType)) {
            paymentType = [paymentType];
        }
        return paymentType.every(p => this.allowedPaymentTypes().some(type => type.key === p));
    }
    /**
     * Whether a quick-pay control may offer this payment type. Follows the
     * register settings, except that cash and card stay available where the
     * market lets a limited register take them (`cashRegister.allowLimitedPayments`):
     * OrderValidation accepts such orders and the fiscal layer reports the card
     * amount as cash, so hiding the button would remove a supported payment.
     */
    allowsQuickPayment(paymentType) {
        const limitedCarveOut = [PaymentTypeLabel.cash, PaymentTypeLabel.credit_card].includes(paymentType)
            && !!I18n.c('cashRegister.allowLimitedPayments');
        return limitedCarveOut || this.allowsPayment(paymentType);
    }
    getOpenShift() {
        return Shift.all().find(shift => !shift.closed_at && shift.cash_register_id === this.id);
    }
    /**
     * Get the cash register active on this device
     */
    static current() {
        return Store.get('cashRegister') || undefined;
    }
}
__decorate([
    LocaleOverride
], CashRegister.prototype, "relevantTransactions", null);
__decorate([
    LocaleOverride
], CashRegister.prototype, "relevantOrders", null);
__decorate([
    LocaleOverride
], CashRegister, "availablePaymentTypes", null);
export default CashRegister;
modelRegistry.register('CashRegister', CashRegister);
//# sourceMappingURL=CashRegister.js.map